import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Badge, Button, Modal, StatusHint } from '@indipia/ui-core';
import { ShareHistoryScreen } from '@indipia/shared-features';
import type {
  ShareHistoryRow,
  ShareHistoryRecipient as CommonRecipient,
  ShareHistoryQuery,
  ShareHistoryPageResult,
} from '@indipia/shared-features';
import {
  photoApi,
  type ShareHistoryGroup,
  type ShareHistoryRecipient as PhotoRecipient,
} from '../api/photoApi';
import { PhotoCard } from '../components/PhotoCard';
import {
  apiErrorInfo,
  fetchRevokeImpact,
  resendPhotoShareBatch,
  type RevokeImpact,
} from '../api/shareAdapter';

// 사진 공유이력 — 공용 ShareHistoryScreen(@indipia/shared-features) 기반.
// 공용 화면이 필터·무한스크롤·행 펼침·배지를 그리고, 여기서는 fetchPage(어댑터)·renderDetail만 공급한다.
// 근거: 공유이력_일관화_개발설계서.

const CHANNEL_LABEL: Record<string, string> = {
  SMS:   '문자',
  KAKAO: '카카오',
  EMAIL: '메일',
};

const CHANNEL_STATUS: Record<string, 'info' | 'warning' | 'success'> = {
  SMS:   'info',
  KAKAO: 'warning',
  EMAIL: 'success',
};

// recipientType → 표시 라벨(선택). 미매핑 코드는 라벨 생략.
const RECIPIENT_TYPE_LABEL: Record<string, string> = {
  CUSTOMER: '고객',
  PARTNER:  '반장',
  VENDOR:   '거래처',
};

// 공용 모델 query.dateFrom/dateTo 는 'YYYYMMDD' — BE 가 요구하는 'YYYY-MM-DD' 로 변환
function ymdToDash(ymd: string | undefined): string | undefined {
  if (!ymd || ymd.length !== 8) return undefined;
  return `${ymd.slice(0, 4)}-${ymd.slice(4, 6)}-${ymd.slice(6, 8)}`;
}

function fmtDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '-';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}.${pad(d.getMonth() + 1)}.${pad(d.getDate())}`;
}

// 수신자 recipientType 에 따라 미리보기(새 창) URL 을 구성.
// 고객(CUSTOMER) 은 고객대시보드 미리보기로, 그 외는 개별 뷰어 미리보기로 연다.
// 이력 행 번호(historyId)가 있으면 그 발송 건을, 없으면 shareId 만으로 연다(같은 링크로 여러 번 보냈으면 서버가 가장 최근 발송 건을 연다).
function buildPreviewUrl(recipientType: string, shareId: number, historyId?: number | null): string {
  const origin = window.location.origin;
  const hist = historyId != null ? `&historyId=${historyId}` : '';
  return recipientType === 'CUSTOMER'
    ? `${origin}/customer-home/preview?shareId=${shareId}${hist}`
    : `${origin}/share/PHOTO_ALBUM_SHARE/PREVIEW?shareId=${shareId}${hist}`;
}

function isExpired(expiresDtm: string | null): boolean {
  return expiresDtm != null && new Date(expiresDtm).getTime() < Date.now();
}

// 수신자 공유취소 가능 여부 — 미취소·미만료·shareId 있음
function isRevokable(r: PhotoRecipient): boolean {
  return r.shareId != null && r.revokedYn !== 'Y' && !isExpired(r.expiresDtm);
}

// 그룹의 발송 묶음 번호 — 서버 그룹 키는 shareBatchId, 묶음 번호가 없던 단건은 'single-{historyId}' 라서 재발송 대상이 아니다.
function groupBatchId(group: ShareHistoryGroup): string | null {
  return group.groupKey && !group.groupKey.startsWith('single-') ? group.groupKey : null;
}

// 재발송 가능 여부 — 묶음 번호가 있고, 서버가 수신자 행마다 판정한 resendable 이 하나라도 true 일 때.
function isResendable(group: ShareHistoryGroup): boolean {
  return groupBatchId(group) != null && group.recipients.some((r) => r.resendable);
}

// 서버 resendBlockedReason 코드 → 안내 문구
const RESEND_BLOCKED_TEXT: Record<string, string> = {
  SEND_WINDOW_CLOSED: '공사 종료일 + 7일이 지나 알림톡을 보낼 수 없어요. 링크를 복사해 직접 보내 주세요.',
  EXPIRED:            '링크 유효기간이 지나 재발송할 수 없어요.',
  REVOKED:            '공유가 취소되어 재발송할 수 없어요.',
  NOT_SUPPORTED:      '재발송을 지원하지 않는 발송 건이에요.',
};
const RESEND_BLOCKED_PRIORITY = ['SEND_WINDOW_CLOSED', 'EXPIRED', 'REVOKED', 'NOT_SUPPORTED'];

// 재발송 불가 안내 — 모든 수신자가 불가일 때 가장 알려 줄 만한 사유 하나(없으면 null)
function resendBlockedText(group: ShareHistoryGroup): string | null {
  const reasons = group.recipients.map((r) => r.resendBlockedReason).filter((x): x is string => !!x);
  if (reasons.length === 0) return null;
  const code = RESEND_BLOCKED_PRIORITY.find((c) => reasons.includes(c)) ?? reasons[0];
  return RESEND_BLOCKED_TEXT[code] ?? null;
}

// 영향 조회 결과 → 모달 안내 문장(정보 제공만, 취소를 막지 않는다)
// 서버 otherMenus[].menu 는 sourceService 코드(예: sv-indipia=스케줄 공유) — 모르는 코드는 그대로 보여 준다.
const SHARE_MENU_LABEL: Record<string, string> = {
  'sv-indipia': '스케줄 공유', 'sv-photo': '사진 공유', 'sv-worklog': '공사현황 공유',
  SCHEDULE: '스케줄 공유', PHOTO: '사진 공유', WORKLOG: '공사현황 공유', WORK_LOG: '공사현황 공유',
};

function fmtDateTime(s: string | null | undefined): string {
  if (!s) return '-';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '-';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}.${pad(d.getMonth() + 1)}.${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function impactLines(d: RevokeImpact): string[] {
  const lines: string[] = [];
  if (!d.linkActive) lines.push('이미 취소되었거나 만료된 링크예요.');
  if (d.sameLinkHistoryCount > 1) lines.push(`같은 링크로 보낸 발송 ${d.sameLinkHistoryCount}건이 모두 열 수 없게 돼요.`);
  for (const m of d.otherMenus ?? []) {
    if (m.historyCount > 0) lines.push(`${SHARE_MENU_LABEL[m.menu] ?? m.menu}에서 같은 링크로 보낸 ${m.historyCount}건도 열 수 없게 돼요.`);
  }
  if (d.viewCount > 0) {
    lines.push(`이 링크는 ${d.viewCount}번 열렸어요${d.lastViewedDtm ? ` (마지막 ${fmtDateTime(d.lastViewedDtm)})` : ''}.`);
  }
  return lines;
}

type ImpactState =
  | { status: 'loading' }
  | { status: 'ready'; data: RevokeImpact }
  | { status: 'error' };

// ShareHistoryGroup → 공용 ShareHistoryRow
function toRow(group: ShareHistoryGroup): ShareHistoryRow {
  const recipients: CommonRecipient[] = group.recipients.map((r) => ({
    recipientId: r.shareId ?? undefined,
    historyId: r.historyId ?? undefined,
    name: r.recipientName,
    role: RECIPIENT_TYPE_LABEL[r.recipientType] ?? null,
    phone: r.shareRecipient,
    channel: r.shareChannel,
    viewed: !!r.viewedDtm,
    viewedAt: r.viewedDtm,
    shareState: r.revokedYn === 'Y' ? 'REVOKED' : isExpired(r.expiresDtm) ? 'EXPIRED' : 'NORMAL',
    shareId: r.shareId != null ? String(r.shareId) : null,
    previewUrl: r.shareId != null ? buildPreviewUrl(r.recipientType, r.shareId, r.historyId) : null,
    resendable: groupBatchId(group) != null && r.resendable,
  }));

  return {
    id: group.groupKey,
    // 사진은 단일 유형 → typeCode 생략
    siteName: group.photosBySite?.[0]?.siteName ?? null,
    sentAt: group.sentDtm,
    // 수신자 expiresDtm 대표값(첫 유효값)
    expiresAt: group.recipients.find((r) => r.expiresDtm)?.expiresDtm ?? null,
    recipients,
    resendable: isResendable(group),
    revokable: group.recipients.some(isRevokable),
    raw: group,
  };
}

export function ShareHistoryMain() {
  // ── 어댑터: 서버 페이지를 끝까지 이어 받아(전량) → 정렬·viewStatus 필터 후 클라이언트 청크(page/size) ──
  const fetchPage = useCallback(
    async (query: ShareHistoryQuery): Promise<ShareHistoryPageResult> => {
      let groups = await photoApi.getShareHistory({
        startDate: ymdToDash(query.dateFrom),
        endDate:   ymdToDash(query.dateTo),
        keyword:   query.keyword,
      });

      // viewStatus 필터(클라이언트). 가정: VIEWED=수신자 중 1명 이상 열람, NOT_VIEWED=수신자 전원 미열람.
      if (query.viewStatus === 'VIEWED') {
        groups = groups.filter((g) => g.recipients.some((r) => !!r.viewedDtm));
      } else if (query.viewStatus === 'NOT_VIEWED') {
        groups = groups.filter((g) => g.recipients.every((r) => !r.viewedDtm));
      }

      // 발송일(sentDtm) 기준 정렬
      const sorted = [...groups].sort((a, b) => {
        const diff = new Date(a.sentDtm).getTime() - new Date(b.sentDtm).getTime();
        return query.sortDesc ? -diff : diff;
      });

      const start = query.page * query.size;
      const pageGroups = sorted.slice(start, start + query.size);
      return {
        items: pageGroups.map(toRow),
        hasNext: (query.page + 1) * query.size < sorted.length,
      };
    },
    [],
  );

  // 모달 확인 등 상세 바깥에서 쓰는 재조회 — 상세가 렌더될 때마다 최신 reload 를 ref 에 보관한다.
  const reloadRef = useRef<() => void>(() => {});

  // ── 공유취소 확인 모달 — 영향을 보여 주되 취소를 막지 않는다 ──
  const [revokeTarget, setRevokeTarget] = useState<{ shareId: number; name: string } | null>(null);
  const [revoking, setRevoking] = useState(false);
  const [impact, setImpact] = useState<ImpactState>({ status: 'loading' });

  // 모달이 열리면 영향을 조회한다 — 서버 API 가 없거나 실패해도 영향 표시만 빠지고 취소는 가능하다.
  useEffect(() => {
    if (!revokeTarget) return;
    const controller = new AbortController();
    setImpact({ status: 'loading' });
    fetchRevokeImpact(revokeTarget.shareId, controller.signal)
      .then((data) => setImpact({ status: 'ready', data }))
      .catch((err: { name?: string }) => {
        if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
        setImpact({ status: 'error' });
      });
    return () => controller.abort();
  }, [revokeTarget]);

  const closeRevoke = () => { if (!revoking) setRevokeTarget(null); };
  const confirmRevoke = async () => {
    const target = revokeTarget;
    if (!target || revoking) return;
    setRevoking(true);
    try {
      await photoApi.revokeShare(target.shareId);
      toast.success('공유를 취소했습니다.');
      setRevokeTarget(null);
      reloadRef.current();
    } catch (err) {
      if (apiErrorInfo(err).status === 409) {
        toast.error('이미 취소된 링크입니다.');
        setRevokeTarget(null);
        reloadRef.current();
      } else {
        toast.error('공유 취소 중 오류가 발생했습니다.');
      }
    } finally {
      setRevoking(false);
    }
  };

  // ── 재발송 확인 모달 — 그룹(shareBatchId) 단위, 서버가 취소·만료 제외 원 수신자에게 새 링크로 다시 보낸다 ──
  const [resendTarget, setResendTarget] = useState<ShareHistoryGroup | null>(null);
  const [resending, setResending] = useState(false);
  const closeResend = () => { if (!resending) setResendTarget(null); };
  const confirmResend = async () => {
    const target = resendTarget;
    const batchId = target ? groupBatchId(target) : null;
    if (!target || !batchId || resending) return;
    setResending(true);
    try {
      const result = await resendPhotoShareBatch(batchId);
      const failed = result.failedCount ?? 0;
      const sent = result.sentCount ?? (failed > 0 ? 0 : 1);
      if (sent === 0 && failed > 0) {
        toast.error('재발송에 실패했어요. 잠시 후 다시 시도해 주세요.');
      } else {
        setResendTarget(null);
        toast.success('재발송 알림톡을 발송했습니다.');
      }
    } catch (err) {
      toast.error(apiErrorInfo(err).message || '재발송 중 오류가 발생했어요.');
    } finally {
      reloadRef.current();
      setResending(false);
    }
  };

  // ── 상세: 수신자별 상태 + 미리보기/공유취소 + 사진 썸네일 그리드 ──
  const renderDetail = useCallback((row: ShareHistoryRow, { reload }: { reload: () => void }) => {
    reloadRef.current = reload;
    const group = row.raw as ShareHistoryGroup;
    const resendable = isResendable(group);
    const blockedText = resendable ? null : resendBlockedText(group);
    const allPhotos = (group.photosBySite ?? []).flatMap((sg) => sg.photos);

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap-md)', padding: '12px 4px' }}>

        {/* 재발송 — 묶음 단위. 가능하지 않으면 서버가 준 사유만 안내한다 */}
        {(resendable || blockedText) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {resendable && (
              <Button variant="etc" size="sm" onClick={() => setResendTarget(group)} style={{ whiteSpace: 'nowrap' }}>
                재발송
              </Button>
            )}
            {blockedText && <StatusHint tone="muted">{blockedText}</StatusHint>}
          </div>
        )}

        {/* 수신자 행 — 이름·연락처·채널·유효기간·미리보기·공유취소 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {group.recipients.map((r, i) => {
            const chStatus = CHANNEL_STATUS[r.shareChannel ?? ''] ?? CHANNEL_STATUS['SMS'];
            const revoked = r.revokedYn === 'Y';
            const expired = !revoked && isExpired(r.expiresDtm);
            const previewUrl = r.shareId != null ? buildPreviewUrl(r.recipientType, r.shareId, r.historyId) : null;
            return (
              <div key={r.shareId ?? i} style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  {revoked && <Badge status="danger" className="whitespace-nowrap">취소</Badge>}
                  {expired && <Badge status="mute" className="whitespace-nowrap">만료</Badge>}
                  {r.viewedDtm ? (
                    <Badge status="success" className="whitespace-nowrap" title={`열람일시 ${fmtDate(r.viewedDtm)}`}>열람됨</Badge>
                  ) : (
                    <Badge status="warning" className="whitespace-nowrap">미열람</Badge>
                  )}
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--shell-hint)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                  <span style={{ fontSize: 'var(--type-caption-size)', fontWeight: 700, color: 'var(--shell-on-surface)', whiteSpace: 'nowrap' }}>{r.recipientName}</span>
                </div>
                {r.shareRecipient && (
                  <span style={{ fontSize: 12, color: 'var(--shell-on-surface-2)', whiteSpace: 'nowrap' }}>{r.shareRecipient}</span>
                )}
                {r.shareChannel && (
                  <Badge status={chStatus} className="whitespace-nowrap">
                    {CHANNEL_LABEL[r.shareChannel] ?? r.shareChannel}
                  </Badge>
                )}
                <span style={{ fontSize: 'var(--type-body-sm-size)', color: 'var(--shell-hint)', whiteSpace: 'nowrap' }}>
                  {fmtDate(group.sentDtm)} ~ {fmtDate(r.expiresDtm)}
                </span>
                {previewUrl && (
                  <Button variant="etc" size="sm" onClick={() => window.open(previewUrl, '_blank', 'noopener')} style={{ whiteSpace: 'nowrap' }}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    미리보기
                  </Button>
                )}
                {r.shareId != null && !revoked && !expired && (
                  <Button variant="etc" size="sm" onClick={() => setRevokeTarget({ shareId: r.shareId!, name: r.recipientName })} style={{ whiteSpace: 'nowrap' }}>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>
                    공유취소
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        {/* 사진 썸네일 그리드 */}
        {allPhotos.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
            {allPhotos.map((photo) => (
              <PhotoCard
                key={photo.photoId}
                photoId={photo.photoId}
                thumbUrl={photo.thumbUrl}
                takenAt={photo.takenAt}
                registeredAt={photo.registeredAt}
                showCheckbox={false}
                showTags={false}
              />
            ))}
            {group.photoCount > allPhotos.length && (
              <div style={{ aspectRatio: '1 / 1', background: 'var(--shell-bg)', border: '1.5px solid var(--shell-border)', borderRadius: 'var(--radius-md)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--type-caption-size)', fontWeight: 700, color: 'var(--shell-hint)' }}>
                +{group.photoCount - allPhotos.length}장
              </div>
            )}
          </div>
        )}
      </div>
    );
  }, []);

  const impactHints = impact.status === 'ready' ? impactLines(impact.data) : [];
  const resendTargets = resendTarget ? resendTarget.recipients.filter((r) => r.resendable) : [];

  return (
    <>
      <ShareHistoryScreen
        title="사진 공유 이력"
        description="고객·거래처에게 보낸 사진 공유 내역의 열람 여부를 확인하고, 필요 시 재발송하거나 공유를 취소할 수 있습니다."
        keywordPlaceholder="제목, 수신인명, 연락처, 이메일"
        fetchPage={fetchPage}
        renderDetail={renderDetail}
      />

      {/* 공유취소 확인 모달 */}
      <Modal
        open={revokeTarget != null}
        onClose={closeRevoke}
        title="공유취소"
        footerAlign="stretch"
        footer={
          <>
            <Button variant="secondary" disabled={revoking} onClick={closeRevoke}>닫기</Button>
            <Button variant="danger" loading={revoking} onClick={() => void confirmRevoke()}>공유취소</Button>
          </>
        }
      >
        {revokeTarget && (
          <div className="flex flex-col">
            <p className="text-body-sm text-text">
              <b>{revokeTarget.name}</b>님께 보낸 링크를 취소하면 받은 분은 더 이상 열 수 없어요.
            </p>
            <div className="mt-1 flex flex-col gap-1">
              {impact.status === 'loading' && <StatusHint tone="muted">영향 범위를 확인하고 있어요.</StatusHint>}
              {impact.status === 'error' && <StatusHint tone="muted">영향 범위를 불러오지 못했어요. 취소는 계속할 수 있어요.</StatusHint>}
              {impactHints.map((t) => <StatusHint key={t} tone="warning">{t}</StatusHint>)}
            </div>
          </div>
        )}
      </Modal>

      {/* 재발송 확인 모달 */}
      <Modal
        open={resendTarget != null}
        onClose={closeResend}
        title="재발송 확인"
        footerAlign="stretch"
        footer={
          <>
            <Button variant="secondary" disabled={resending} onClick={closeResend}>취소</Button>
            <Button variant="primary" loading={resending} onClick={() => void confirmResend()}>
              {resending ? '발송 중...' : '재발송하기'}
            </Button>
          </>
        }
      >
        {resendTarget && (
          <div className="flex flex-col">
            <p className="text-body-sm text-text">
              {resendTargets.length > 0
                ? <><b>{resendTargets.map((r) => r.recipientName).join(', ')}</b>님께 같은 사진첩을 알림톡으로 다시 보내요.</>
                : '이 발송 건의 수신자에게 같은 사진첩을 알림톡으로 다시 보내요.'}
            </p>
            <div className="mt-1 flex flex-col gap-1">
              <StatusHint tone="info">취소되었거나 만료된 수신자는 제외되고, 새 링크가 발급돼요.</StatusHint>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}
